import React from 'react';

import { useIntl } from 'react-intl-next';
import { useFragment } from 'react-relay';
import { useRouterActions } from 'react-resource-router';
import { graphql } from 'relay-runtime';

import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import PageIcon from '@atlaskit/icon/core/page';
import { HelpPageAri } from '@atlassian/ari/help';

import type { moreDropdown_AtlassianHubAppShell$key } from './__generated__/moreDropdown_AtlassianHubAppShell.graphql';
import messages from './messages';

export const MoreDropdown = ({
	queryRef,
	textColor,
}: {
	queryRef: moreDropdown_AtlassianHubAppShell$key | null;
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();

	const data = useFragment(
		graphql`
			fragment moreDropdown_AtlassianHubAppShell on HelpCenter {
				customPages: pages(filter: { ids: [], type: CUSTOM }) {
					id
					name {
						default
					}
				}
			}
		`,
		queryRef,
	);

	const customPages = data?.customPages;

	if (!customPages || customPages.length === 0) {
		return null;
	}

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					appearance="subtle"
					style={{ color: props.isSelected ? undefined : textColor }}
					iconAfter={<ChevronDownIcon label={formatMessage(messages.moreDownIcon)} />}
					ref={triggerRef}
				>
					{formatMessage(messages.more)}
				</Button>
			)}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{customPages.map((page) => {
					const pageId = page?.id ? HelpPageAri.parse(page.id).pageId : '';
					return (
						<DropdownItem
							key={pageId}
							testId={`nav-custom-page-${pageId}`}
							elemBefore={<PageIcon label={formatMessage(messages.pageIconLabel)} />}
							onClick={() => {
								push(`/page/${pageId}`);
							}}
						>
							{page?.name?.default}
						</DropdownItem>
					);
				})}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
